import { Capacitor } from '@capacitor/core';
import { createRouter, createWebHistory } from '@ionic/vue-router';
import { isPlatform } from '@ionic/vue';
import type { RouteRecordRaw } from 'vue-router';
import { applyAuthMiddleware } from './middleware';

const AUTH_STORAGE_KEY = 'swapwish.auth';

function hasPersistedAuthSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed;
    return !!(session && (session.access_token || session.refresh_token));
  } catch {
    return false;
  }
}
function isInstalledAppRuntime(): boolean {
  if (Capacitor.isNativePlatform() || isPlatform('capacitor')) {
    return true;
  }

  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent.toLowerCase();
    return /webview|wv|capacitor/i.test(ua) || /\b; wv\b/i.test(ua);
  }

  return false;
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    // Web → landing. App installata: a sessione già attiva si va direttamente
    // alla schermata di caricamento post-auth (/loading), evitando il flash del
    // form di login; altrimenti al login.
    redirect: () => {
      if (!isInstalledAppRuntime()) return '/landing';
      // A sessione persistita (anche prima del ripristino async) si va al loader:
      // evita il flash del form di login. /loading verifica la sessione reale e,
      // se non valida, rimanda a /login.
      return hasPersistedAuthSession() ? '/loading' : '/login';
    },
  },
  { path: '/landing', component: () => import('@/views/LandingView.vue'), meta: { webOnly: true } },
  { path: '/login', component: () => import('@/views/LoginView.vue') },
  // Caricamento POST-auth (prefetch espansioni + preload dati utente) prima di
  // entrare in app. Protetta dal guard: solo utenti autenticati la raggiungono.
  { path: '/loading', component: () => import('@/views/AppLoadingView.vue') },
  { path: '/auth/callback', component: () => import('@/views/AuthCallbackView.vue') },
  // Impostazione nuova password dopo il link di recupero (sessione di recovery).
  { path: '/auth/reset-password', component: () => import('@/views/ResetPasswordView.vue') },
  // Norme sulla privacy: rotta PUBBLICA (linkata dal footer della landing,
  // consultabile anche senza account). Dall'app si usa /tabs/profile/privacy.
  { path: '/privacy', component: () => import('@/views/PrivacyPolicyView.vue') },
  // Landing PUBBLICA del link d'invito referral (web / universal link): cattura
  // il codice e instrada a login/app. Vedi InviteCaptureView.
  { path: '/invite/:code', component: () => import('@/views/InviteCaptureView.vue') },
  // Pagina PUBBLICA del profilo condiviso (inventario cedibile + wishlist),
  // risolta da un share_token revocabile. Vedi SharedProfileView.
  { path: '/s/:token', component: () => import('@/views/SharedProfileView.vue') },
  // Cancellazione account: rotta PUBBLICA richiesta da Google Play Console come
  // "URL per l'eliminazione dell'account" (consultabile senza login). Dall'app
  // si usa /tabs/profile/delete-account.
  { path: '/delete-account', component: () => import('@/views/DeleteAccountView.vue') },
  // Standard di sicurezza dei minori (CSAE): rotta PUBBLICA richiesta da Google
  // Play Console nella dichiarazione "Sicurezza dei minori".
  { path: '/child-safety', component: () => import('@/views/ChildSafetyView.vue') },
  // Termini di servizio: rotta PUBBLICA linkata dal footer della landing.
  { path: '/terms', component: () => import('@/views/TermsView.vue') },
  {
    path: '/tabs',
    component: () => import('@/views/TabsLayout.vue'),
    children: [
      { path: '', redirect: '/tabs/map' },
      { path: 'map', component: () => import('@/views/MapView.vue') },
      { path: 'swipe', redirect: '/tabs/map' },
      { path: 'inventory', component: () => import('@/views/InventoryView.vue') },
      { path: 'wishlist', component: () => import('@/views/WishlistView.vue') },
      { path: 'matches', component: () => import('@/views/MatchesView.vue') },
      { path: 'notifications', component: () => import('@/views/NotificationsView.vue') },
      { path: 'matches/history', component: () => import('@/views/HistoryTradesView.vue') },
      { path: 'matches/:id', component: () => import('@/views/ChatView.vue') },
      { path: 'profile', component: () => import('@/views/ProfileView.vue') },
      { path: 'profile/donate', component: () => import('@/views/DonateView.vue') },
      { path: 'profile/invite', component: () => import('@/views/InviteFriendView.vue') },
      { path: 'profile/privacy', component: () => import('@/views/PrivacyPolicyView.vue') },
      { path: 'profile/delete-account', component: () => import('@/views/DeleteAccountView.vue') },
      { path: 'profile/child-safety', component: () => import('@/views/ChildSafetyView.vue') },
      { path: 'profile/terms', component: () => import('@/views/TermsView.vue') },
      // Funzionalità utenti verificati: verifica, tornei, banner + admin.
      // "I miei tornei" (partecipante): iscrizioni attive + storico con vittorie.
      { path: 'profile/my-tournaments', component: () => import('@/views/MyTournamentsView.vue') },
      // Vetrina del profilo pubblico condiviso (cerco/offro in evidenza).
      { path: 'profile/showcase', component: () => import('@/views/ShowcaseEditView.vue') },
      { path: 'profile/verify', component: () => import('@/views/RequestVerificationView.vue') },
      { path: 'profile/verified-features', component: () => import('@/views/VerifiedFeaturesView.vue') },
      { path: 'profile/banner', component: () => import('@/views/ConfigureBannerView.vue') },
      { path: 'profile/tournaments', component: () => import('@/views/ManageTournamentsView.vue') },
      { path: 'profile/admin/banners', component: () => import('@/views/AdminBannersView.vue') },
      { path: 'profile/admin/verifications', component: () => import('@/views/AdminVerificationsView.vue') },
      { path: 'catalog', component: () => import('@/views/CatalogView.vue') },
      { path: 'expansion', component: () => import('@/views/ExpansionView.vue') },
      { path: 'deck/:id', component: () => import('@/views/DeckEditView.vue') },
      { path: 'settings', component: () => import('@/views/SettingsView.vue') },
      { path: 'settings/help', component: () => import('@/views/HelpView.vue') },
      { path: 'settings/contacts', component: () => import('@/views/ContactsView.vue') },
      { path: 'settings/subscription', component: () => import('@/views/SubscriptionView.vue') },
      { path: 'three-way', redirect: '/tabs/map?triples=1' },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

applyAuthMiddleware(router);

router.beforeEach((to) => {
  // Sessione persistita: evita il form di login anche prima che lo store utente
  // completi l'idratazione async. /loading verificherà la sessione reale.
  if (to.path === '/login' && hasPersistedAuthSession()) return '/loading';

  if (!isInstalledAppRuntime()) return true;

  if (to.path === '/landing' || to.meta.webOnly) {
    return '/login';
  }

  return true;
});

// After each navigation, schedule a lightweight check that clears any
// pointer-events: none left on ion-router-outlet by an interrupted transition.
// Run three times (400 ms + 900 ms + 2000 ms) to cover fast and slow Android
// WebView animation timings; the later passes are belt-and-suspenders safety
// nets for transitions that end after the earlier checks (when that happens
// the leftover pointer-events:none makes every in-page control unclickable
// while the tab bar keeps working). Views can also invoke it deterministically
// from `ionViewDidEnter` (e.g. MapView), when the transition is over by
// definition and any leftover lock is stale.
export function runIonRecovery() {
  if (typeof document === 'undefined') return;

  // Host ion-modal "spogliati": una riscrittura dell'attributo class (es. un
  // binding :class dinamico che cambia valore dopo il dismiss — Vue sostituisce
  // l'INTERO attributo e cancella le classi runtime di Ionic) lascia il modale
  // senza né show-modal né overlay-hidden: un layer invisibile a schermo intero
  // che intercetta ogni tap della vista sottostante. Un ion-modal legittimo è
  // SEMPRE in uno dei due stati; se mancano entrambi e il modale risulta chiuso
  // (isOpen !== true, per non toccare una presentazione in corso), si
  // ripristina overlay-hidden (display:none) ripristinando l'invariante.
  for (const modal of Array.from(document.querySelectorAll('ion-modal'))) {
    if (
      !modal.classList.contains('show-modal')
      && !modal.classList.contains('overlay-hidden')
      && (modal as HTMLElement & { isOpen?: boolean }).isOpen !== true
    ) {
      modal.classList.add('overlay-hidden');
    }
  }

  // Backdrop orfani: un overlay Ionic (ion-modal/action-sheet/…) chiuso a ridosso
  // di una transizione di vista può lasciare un <ion-backdrop> teleportato alla
  // root con z-index altissimo che intercetta OGNI tap, rendendo la nuova vista
  // "morta" (es. su MapView: lista candidati/filtri/leaderboard non cliccabili pur
  // con la mappa visibile). Finora la rimozione viveva solo in App.vue e scattava
  // al resume dell'app, non all'ingresso in una vista: qui la eseguiamo anche in
  // runIonRecovery (invocato dalle viste in ionViewDidEnter) SOLO quando non c'è
  // alcun overlay realmente aperto, così non si tocca un modale legittimo.
  const hasOpenOverlay = !!document.querySelector(
    'ion-modal.show-modal, ion-alert.show-alert, ion-action-sheet.show-action-sheet, ion-popover.show-popover, ion-loading.show-loading'
  );
  if (!hasOpenOverlay) {
    for (const backdrop of Array.from(document.querySelectorAll('ion-backdrop'))) {
      backdrop.remove();
    }
  }

  for (const outlet of Array.from(document.querySelectorAll('ion-router-outlet')) as HTMLElement[]) {
    if (outlet.style.pointerEvents === 'none') outlet.style.pointerEvents = '';
    // `inert` (usato dalle versioni recenti di Ionic sulle pagine/outlet
    // nascosti) blocca OGNI input, non solo la navigazione assistiva.
    outlet.removeAttribute('inert');
    outlet.removeAttribute('aria-hidden');

    // Mobile/WebView safeguard: interrupted transitions can leave the current
    // routed page flagged as hidden (`ion-page-hidden` / `ion-page-invisible`),
    // resulting in a white screen even though the DOM is mounted.
    // SOLO figli DIRETTI dell'outlet: querySelectorAll('.ion-page') scendeva
    // anche nelle pagine ANNIDATE (es. le viste tab dentro TabsLayout viste
    // dall'outlet esterno) e il re-hide sotto finiva per nascondere il
    // contenitore dei tab stesso → app montata ma nessuna vista visibile.
    const pages = (Array.from(outlet.children) as HTMLElement[])
      .filter((el) => el.classList?.contains('ion-page'));
    if (!pages.length) continue;

    const visiblePages = pages.filter((page) =>
      !page.classList.contains('ion-page-hidden') && !page.classList.contains('ion-page-invisible')
    );
    // Transizione ancora in corso (due pagine visibili e l'ultima sta ancora
    // entrando): non toccare nulla, ci pensano i passaggi successivi. Senza
    // questa guardia il passaggio a 400 ms poteva "de-nascondere" entrambe le
    // pagine a metà slide, lasciando la vecchia vista sovrapposta alla nuova.
    if (visiblePages.length > 1
      && pages[pages.length - 1]?.classList.contains('ion-page-invisible')) {
      continue;
    }

    // Se NESSUNA pagina è attualmente visibile siamo quasi sempre a metà di una
    // transizione (Ionic ha già nascosto l'uscente ma non ancora rivelato
    // l'entrante). In quel caso NON si deve indovinare la pagina attiva
    // dall'ordine del DOM: `pages[pages.length - 1]` è l'ULTIMA tab visitata per
    // prima, non la destinazione della navigazione, e forzarla visibile montava a
    // intermittenza la vista sbagliata (es. si va in Inventario ma appare Matches).
    // Si esce: le passate successive (900/2000 ms) interverranno a transizione
    // conclusa, quando c'è una pagina visibile autorevole.
    const activePage = visiblePages[visiblePages.length - 1];
    if (!activePage) continue;

    activePage.classList.remove('ion-page-hidden', 'ion-page-invisible');
    if (activePage.style.display === 'none') activePage.style.display = '';
    if (activePage.style.visibility === 'hidden') activePage.style.visibility = '';
    if (activePage.style.pointerEvents === 'none') activePage.style.pointerEvents = '';
    activePage.removeAttribute('aria-hidden');
    activePage.removeAttribute('inert');

    // Re-nascondi le pagine stantie: il recupero de-nascondeva solo la pagina
    // attiva ma non ripristinava `ion-page-hidden` sulle altre, quindi la vista
    // precedente poteva restare visibile insieme alla nuova (overlap sporadico
    // segnalato nelle transizioni Inventario/Wishlist → Catalogo → Espansione).
    for (const page of pages) {
      if (page !== activePage && !page.classList.contains('ion-page-hidden')) {
        page.classList.add('ion-page-hidden');
      }
    }
  }
}

let _recoveryTimer1: ReturnType<typeof setTimeout> | null = null;
let _recoveryTimer2: ReturnType<typeof setTimeout> | null = null;
let _recoveryTimer3: ReturnType<typeof setTimeout> | null = null;
router.afterEach(() => {
  if (_recoveryTimer1) clearTimeout(_recoveryTimer1);
  if (_recoveryTimer2) clearTimeout(_recoveryTimer2);
  if (_recoveryTimer3) clearTimeout(_recoveryTimer3);
  // 700 ms: oltre la durata tipica dello slide, così il primo passaggio non
  // intercetta una transizione legittima ancora in corso (WebView lente).
  _recoveryTimer1 = setTimeout(() => { _recoveryTimer1 = null; runIonRecovery(); }, 700);
  _recoveryTimer2 = setTimeout(() => { _recoveryTimer2 = null; runIonRecovery(); }, 900);
  _recoveryTimer3 = setTimeout(() => { _recoveryTimer3 = null; runIonRecovery(); }, 2000);
});

export default router;
